import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Calculator, Save, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { billingAPI, devopsAPI } from '../lib/api';
import { toast } from 'sonner';

const defaultRemarks = `Thank you for your business.

This invoice covers IT services as per the agreed scope of work.
All amounts are in Indian Rupees (INR) and inclusive of applicable GST.

For any queries, please contact our accounts team at accounts@vibe6.com`;

export default function NewInvoicePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_billing_address: '',
    customer_gstin: '',
    customer_state_code: '',  // For GST calculation
    vibe6_bears_gst: false,   // When true, Vibe6 absorbs GST - customer pays entered price only
    epic_id: '',
    epic_name: '',
    feature_ids: [],
    feature_names: [],
    project_display_name: '',
    billing_model: 'time_and_material',
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    remarks_customer: defaultRemarks,
    line_items: [
      { description: '', period: '', quantity: 0, unit: 'hours', unit_price: 0, tax_rate: 18, hsn_sac_code: '998314' }
    ]
  });

  const [totals, setTotals] = useState({ subtotal: 0, tax: 0, total: 0, cgst: 0, sgst: 0, igst: 0, vibe6GstBurden: 0 });
  const [companyStateCode, setCompanyStateCode] = useState('27'); // Default: Maharashtra

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    calculateTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.line_items, formData.vibe6_bears_gst, formData.customer_state_code, companyStateCode]);

  useEffect(() => {
    // Set due date 30 days from invoice date
    if (formData.invoice_date) {
      const invDate = new Date(formData.invoice_date);
      invDate.setDate(invDate.getDate() + 30);
      setFormData(prev => ({ ...prev, due_date: invDate.toISOString().slice(0, 10) }));
    }
  }, [formData.invoice_date]);

  const fetchProjects = async () => {
    try {
      const { data } = await billingAPI.getProjectsFromDevOps();
      setProjects(data.projects || []);
    } catch (error) {
      console.log('DevOps projects not available');
    }
  };

  const handleProjectSelect = (epicId) => {
    const project = projects.find(p => p.epic_id === epicId);
    if (project) {
      setSelectedProject(project);
      setFormData(prev => ({
        ...prev,
        epic_id: project.epic_id,
        epic_name: project.epic_name,
        feature_ids: project.features.map(f => f.id),
        feature_names: project.features.map(f => f.name),
        project_display_name: project.default_project_display_name
      }));
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let tax = 0;
    
    formData.line_items.forEach(item => {
      const amount = (item.quantity || 0) * (item.unit_price || 0);
      const itemTax = amount * (item.tax_rate || 18) / 100;
      subtotal += amount;
      tax += itemTax;
    });
    
    // Determine if interstate (IGST) or intrastate (CGST+SGST)
    const isInterstate = formData.customer_state_code && formData.customer_state_code !== companyStateCode;
    
    const roundedSubtotal = Math.round(subtotal * 100) / 100;
    const roundedTax = Math.round(tax * 100) / 100;
    
    // When Vibe6 bears GST: Customer pays only subtotal, Vibe6 absorbs GST
    // When Vibe6 does NOT bear GST (default): Customer pays subtotal + GST
    const customerTotal = formData.vibe6_bears_gst 
      ? roundedSubtotal  // Customer pays entered price only
      : Math.round((subtotal + tax) * 100) / 100;  // Customer pays price + GST
    
    setTotals({
      subtotal: roundedSubtotal,
      tax: roundedTax,
      total: customerTotal,
      cgst: isInterstate ? 0 : Math.round(tax / 2 * 100) / 100,
      sgst: isInterstate ? 0 : Math.round(tax / 2 * 100) / 100,
      igst: isInterstate ? roundedTax : 0,
      isInterstate,
      vibe6GstBurden: formData.vibe6_bears_gst ? roundedTax : 0  // GST absorbed by Vibe6
    });
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        { description: '', period: '', quantity: 0, unit: 'hours', unit_price: 0, tax_rate: 18 }
      ]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSubmit = async (sendAfterSave = false) => {
    if (!formData.customer_name || !formData.customer_email || !formData.project_display_name) {
      toast.error('Please fill in required fields');
      return;
    }
    
    if (formData.line_items.some(item => !item.description || item.quantity <= 0)) {
      toast.error('Please complete all line items');
      return;
    }
    
    setLoading(true);
    try {
      const { data: invoice } = await billingAPI.createInvoice(formData);
      
      if (sendAfterSave) {
        await billingAPI.sendInvoice(invoice.id);
        toast.success('Invoice created and sent!');
      } else {
        toast.success('Invoice saved as draft');
      }
      
      navigate('/billing/invoices');
    } catch (error) {
      toast.error('Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/billing/invoices')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">New Invoice</h1>
          <p className="text-muted-foreground">Create a new customer invoice</p>
        </div>
      </div>

      {/* Project Selection */}
      {projects.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Link to DevOps Project (Optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <Select onValueChange={handleProjectSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a project from Azure DevOps" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.epic_id} value={p.epic_id}>
                    {p.epic_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Customer Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Customer Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                placeholder="Company Name Pvt Ltd"
              />
            </div>
            <div>
              <Label>Customer Email *</Label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                placeholder="accounts@customer.com"
              />
            </div>
          </div>
          <div>
            <Label>Billing Address</Label>
            <Textarea
              value={formData.customer_billing_address}
              onChange={(e) => setFormData(prev => ({ ...prev, customer_billing_address: e.target.value }))}
              placeholder="Street, City, State, PIN"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>GSTIN (optional)</Label>
              <Input
                value={formData.customer_gstin}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_gstin: e.target.value }))}
                placeholder="22AAAAA0000A1Z5"
              />
            </div>
            <div>
              <Label>Customer State</Label>
              <Select 
                value={formData.customer_state_code} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, customer_state_code: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state (for GST)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="01">Jammu and Kashmir</SelectItem>
                  <SelectItem value="02">Himachal Pradesh</SelectItem>
                  <SelectItem value="03">Punjab</SelectItem>
                  <SelectItem value="04">Chandigarh</SelectItem>
                  <SelectItem value="05">Uttarakhand</SelectItem>
                  <SelectItem value="06">Haryana</SelectItem>
                  <SelectItem value="07">Delhi</SelectItem>
                  <SelectItem value="08">Rajasthan</SelectItem>
                  <SelectItem value="09">Uttar Pradesh</SelectItem>
                  <SelectItem value="10">Bihar</SelectItem>
                  <SelectItem value="19">West Bengal</SelectItem>
                  <SelectItem value="21">Odisha</SelectItem>
                  <SelectItem value="22">Chhattisgarh</SelectItem>
                  <SelectItem value="23">Madhya Pradesh</SelectItem>
                  <SelectItem value="24">Gujarat</SelectItem>
                  <SelectItem value="27">Maharashtra</SelectItem>
                  <SelectItem value="29">Karnataka</SelectItem>
                  <SelectItem value="30">Goa</SelectItem>
                  <SelectItem value="32">Kerala</SelectItem>
                  <SelectItem value="33">Tamil Nadu</SelectItem>
                  <SelectItem value="36">Telangana</SelectItem>
                  <SelectItem value="37">Andhra Pradesh</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="vibe6_bears_gst"
                checked={formData.vibe6_bears_gst}
                onChange={(e) => setFormData(prev => ({ ...prev, vibe6_bears_gst: e.target.checked }))}
                className="w-4 h-4"
              />
              <Label htmlFor="vibe6_bears_gst" className="cursor-pointer">Vibe6 bears GST</Label>
            </div>
            <span className="text-sm text-muted-foreground">
              {formData.vibe6_bears_gst 
                ? '(GST inclusive - customer pays entered price only)' 
                : '(GST exclusive - GST added on top of price)'}
            </span>
            {formData.customer_state_code && (
              <span className="text-sm text-muted-foreground ml-2">
                • {formData.customer_state_code === companyStateCode 
                  ? 'Intra-state (CGST + SGST)' 
                  : 'Inter-state (IGST)'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Invoice Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Project Name *</Label>
            <Input
              value={formData.project_display_name}
              onChange={(e) => setFormData(prev => ({ ...prev, project_display_name: e.target.value }))}
              placeholder="Epic → Feature (e.g., Customer Portal → Authentication & SSO)"
            />
            <p className="text-xs text-muted-foreground mt-1">This appears on the invoice as the project reference</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Billing Model</Label>
              <Select value={formData.billing_model} onValueChange={(v) => setFormData(prev => ({ ...prev, billing_model: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="time_and_material">Time & Material</SelectItem>
                  <SelectItem value="fixed_price">Fixed Price</SelectItem>
                  <SelectItem value="milestone">Milestone</SelectItem>
                  <SelectItem value="retainer">Retainer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Invoice Date</Label>
              <Input
                type="date"
                value={formData.invoice_date}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Line Items</CardTitle>
          <Button variant="outline" size="sm" onClick={addLineItem}>
            <Plus className="h-4 w-4 mr-1" /> Add Item
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {formData.line_items.map((item, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-start justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Item {index + 1}</span>
                  {formData.line_items.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeLineItem(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Description *</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      placeholder="Development Services - Sprint 5"
                    />
                  </div>
                  <div>
                    <Label>Period</Label>
                    <Input
                      value={item.period}
                      onChange={(e) => updateLineItem(index, 'period', e.target.value)}
                      placeholder="Dec 1-15, 2025"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select value={item.unit} onValueChange={(v) => updateLineItem(index, 'unit', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hours">Hours</SelectItem>
                        <SelectItem value="days">Days</SelectItem>
                        <SelectItem value="units">Units</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Rate (₹)</Label>
                    <Input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>SAC Code</Label>
                    <Input
                      value={item.hsn_sac_code || '998314'}
                      onChange={(e) => updateLineItem(index, 'hsn_sac_code', e.target.value)}
                      placeholder="998314"
                    />
                  </div>
                  <div>
                    <Label>GST %</Label>
                    <Select 
                      value={`${item.tax_rate}`} 
                      onValueChange={(v) => updateLineItem(index, 'tax_rate', parseFloat(v))}
                      disabled={!formData.gst_applicable}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0%</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="18">18%</SelectItem>
                        <SelectItem value="28">28%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount</Label>
                    <div className="h-10 px-3 py-2 bg-muted rounded-md text-sm font-medium">
                      ₹{((item.quantity || 0) * (item.unit_price || 0)).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="mt-6 pt-4 border-t">
            <div className="flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>₹{totals.subtotal.toLocaleString()}</span>
                </div>
                {totals.isInterstate ? (
                  <div className="flex justify-between text-sm">
                    <span>IGST ({formData.line_items[0]?.tax_rate || 18}%):</span>
                    <span>₹{totals.igst.toLocaleString()}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span>CGST ({(formData.line_items[0]?.tax_rate || 18) / 2}%):</span>
                      <span>₹{totals.cgst.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>SGST ({(formData.line_items[0]?.tax_rate || 18) / 2}%):</span>
                      <span>₹{totals.sgst.toLocaleString()}</span>
                    </div>
                  </>
                )}
                {formData.vibe6_bears_gst && (
                  <div className="flex justify-between text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/20 px-2 py-1 rounded">
                    <span>Vibe6 bears GST:</span>
                    <span>-₹{totals.vibe6GstBurden.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Customer Payable:</span>
                  <span>₹{totals.total.toLocaleString()}</span>
                </div>
                {formData.vibe6_bears_gst && (
                  <p className="text-xs text-muted-foreground">
                    * GST of ₹{totals.vibe6GstBurden.toLocaleString()} absorbed by Vibe6
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Remarks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Notes & Remarks</CardTitle>
        </CardHeader>
        <CardContent>
          <Label>Customer Remarks (shown on invoice)</Label>
          <Textarea
            value={formData.remarks_customer}
            onChange={(e) => setFormData(prev => ({ ...prev, remarks_customer: e.target.value }))}
            rows={5}
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/billing/invoices')}>
          Cancel
        </Button>
        <Button variant="outline" onClick={() => handleSubmit(false)} disabled={loading}>
          <Save className="h-4 w-4 mr-2" />
          Save as Draft
        </Button>
        <Button onClick={() => handleSubmit(true)} disabled={loading}>
          <Send className="h-4 w-4 mr-2" />
          Save & Send
        </Button>
      </div>
    </div>
  );
}
